import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { getAiUsage, MAX_AI_DAILY_LIMIT, updateAiUsageLimits, type AiUsage } from '../api/aiUsage';
import { Alert, Spinner } from '../shared/components/feedback';
import { Button } from '../shared/components/Button';
import { Field, Input } from '../shared/components/Field';
import { AiUsageHistory } from './AiUsageHistory';
import { ApiError } from '../api/client';

export function AiUsagePanel({ historyDays, refreshKey = 0 }: { historyDays?: number; refreshKey?: number }) {
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [editing, setEditing] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [tokenLimit, setTokenLimit] = useState('');
  const [imageLimit, setImageLimit] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ tokens: '', images: '' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const requestId = useRef(0);
  const mounted = useRef(true);
  const settingsButton = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  const formId = useId();
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (editing) wasEditing.current = true;
    else if (!saving && wasEditing.current) {
      settingsButton.current?.focus();
      wasEditing.current = false;
    }
  }, [editing, saving]);
  useEffect(() => {
    // A save returns a fresh snapshot; a concurrent report refresh must not replace it.
    if (savingRef.current) return;
    let cancelled = false;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    getAiUsage().then((data) => { if (!cancelled && id === requestId.current) setUsage(data); })
      .catch((err) => { if (!cancelled && id === requestId.current) setError(err instanceof ApiError ? err.description || err.message : '사용량을 불러오지 못했습니다.'); })
      .finally(() => { if (!cancelled && id === requestId.current) setLoading(false); });
    return () => { cancelled = true; };
  }, [refresh, refreshKey]);
  const fmt = (value: number | null | undefined) => (value ?? 0).toLocaleString('ko-KR');
  const beginEditing = () => {
    if (!usage) return;
    if (!hasDraft) {
      setTokenLimit(String(usage.dailyTokenLimit));
      setImageLimit(String(usage.dailyImageLimit));
      setHasDraft(true);
    }
    setFieldErrors({ tokens: '', images: '' });
    setSaveError(null);
    setSaved(false);
    setEditing(true);
  };
  const saveLimits = async (event: FormEvent) => {
    event.preventDefault();
    if (savingRef.current) return;
    const errors = { tokens: validateLimit(tokenLimit), images: validateLimit(imageLimit) };
    setFieldErrors(errors);
    if (errors.tokens || errors.images) return;
    savingRef.current = true;
    ++requestId.current;
    setLoading(false);
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const snapshot = await updateAiUsageLimits({ dailyTokenLimit: Number(tokenLimit.trim()), dailyImageLimit: Number(imageLimit.trim()) });
      if (!mounted.current) return;
      setUsage(snapshot);
      setError(null);
      setEditing(false);
      setHasDraft(false);
      setSaved(true);
    } catch (err) {
      if (mounted.current) setSaveError(err instanceof ApiError ? err.description || err.message : 'AI 한도를 저장하지 못했습니다. 입력값을 확인한 뒤 다시 시도해 주세요.');
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  return (
    <UsageSection aria-label="AI 사용량" aria-busy={loading}>
      <Heading><div><h2>AI 사용량</h2><Help>한도를 설정하지 않으면 제한 없이 사용합니다. 이 사이트에서 저장한 한도만 적용됩니다.</Help></div><HeadingActions>
        <Button ref={settingsButton} type="button" $variant="secondary" $size="sm" disabled={!usage || saving} aria-expanded={editing} aria-controls={`${formId}-limits`} onClick={() => editing ? setEditing(false) : beginEditing()}>한도 설정</Button>
        <Button type="button" $variant="secondary" $size="sm" disabled={loading || saving} onClick={() => setRefresh((n) => n + 1)}>새로고침</Button>
      </HeadingActions></Heading>
      {error && <Alert tone="danger">{error}</Alert>}
      {loading && <LoadingState role="status"><Spinner aria-label="사용량 조회 중" /> 사용량을 불러오고 있습니다.</LoadingState>}
      {saved && <Alert tone="success">AI 한도를 저장했습니다. 다음 AI 요청부터 적용됩니다.</Alert>}
      {editing && <LimitsForm id={`${formId}-limits`} aria-label="AI 한도 설정" aria-busy={saving} onSubmit={saveLimits} noValidate>
        <div><h3>일일 AI 한도</h3><Help id={`${formId}-help`}>전체 계정에 공통으로 적용됩니다. 0은 무제한이며 매일 한국 시간 0시부터 새로 집계합니다.</Help></div>
        <LimitFields>
          <Field label="일일 토큰 한도" htmlFor={`${formId}-tokens`}>
            <Input id={`${formId}-tokens`} type="number" inputMode="numeric" min={0} max={MAX_AI_DAILY_LIMIT} step={1} required autoFocus
              value={tokenLimit} disabled={saving} aria-invalid={!!fieldErrors.tokens} aria-describedby={`${formId}-help${fieldErrors.tokens ? ` ${formId}-tokens-error` : ''}`}
              onChange={(event) => { setTokenLimit(event.target.value); setFieldErrors((current) => ({ ...current, tokens: '' })); setSaveError(null); }} />
            {fieldErrors.tokens && <FieldError id={`${formId}-tokens-error`} role="alert">{fieldErrors.tokens}</FieldError>}
          </Field>
          <Field label="일일 이미지 요청 한도" htmlFor={`${formId}-images`}>
            <Input id={`${formId}-images`} type="number" inputMode="numeric" min={0} max={MAX_AI_DAILY_LIMIT} step={1} required
              value={imageLimit} disabled={saving} aria-invalid={!!fieldErrors.images} aria-describedby={`${formId}-help${fieldErrors.images ? ` ${formId}-images-error` : ''}`}
              onChange={(event) => { setImageLimit(event.target.value); setFieldErrors((current) => ({ ...current, images: '' })); setSaveError(null); }} />
            {fieldErrors.images && <FieldError id={`${formId}-images-error`} role="alert">{fieldErrors.images}</FieldError>}
          </Field>
        </LimitFields>
        <Help>한도를 변경해도 오늘 사용량은 유지됩니다. 사용량보다 낮추면 다음 요청부터 제한되며, 진행 중인 요청은 중단되지 않습니다.</Help>
        {saveError && <Alert tone="danger">{saveError}</Alert>}
        <FormActions>
          <Button type="button" $variant="secondary" $size="sm" disabled={saving} onClick={() => { setEditing(false); setHasDraft(false); }}>취소</Button>
          <Button type="submit" $size="sm" disabled={saving}>{saving ? '저장 중…' : '한도 저장'}</Button>
        </FormActions>
      </LimitsForm>}
      {usage && <>
        <Help>{usage.date} · 한국 시간 기준 · 워크스페이스 전체</Help>
        <Grid>
          <Metric $emphasis><dt>보고된 토큰 합계</dt><dd>{fmt(usage.totalTokens)}</dd></Metric>
          <Metric><dt>오늘 남은 토큰</dt><dd>{usage.dailyTokenLimit === 0 ? '제한 없음' : fmt(usage.remainingTokens)}</dd><MetricNote>{usage.dailyTokenLimit === 0 ? '일일 토큰 제한 미설정' : `일일 한도 ${fmt(usage.dailyTokenLimit)}`}</MetricNote></Metric>
          <Metric><dt>이미지 요청 / 일일 한도</dt><dd>{fmt(usage.imageRequests)} <MetricUnit>/ {usage.dailyImageLimit === 0 ? '제한 없음' : fmt(usage.dailyImageLimit)}</MetricUnit></dd></Metric>
        </Grid>
        <Details><summary>토큰 상세 · 요청 및 캐시 내역</summary><DetailGrid>
          <Metric><dt>입력 / 출력</dt><dd>{fmt(usage.inputTokens)} / {fmt(usage.outputTokens)}</dd></Metric>
          <Metric><dt>입력 중 제공사 캐시</dt><dd>{fmt(usage.cachedInputTokens)}</dd></Metric>
          <Metric><dt>처리 중·사용량 확인 중 토큰</dt><dd>{fmt(usage.reservedTokens)}</dd></Metric>
          <Metric><dt>AI 요청 / 실패</dt><dd>{fmt(usage.requests)} / {fmt(usage.failedRequests)}</dd></Metric>
          <Metric><dt>예산 한도로 차단된 요청</dt><dd>{fmt(usage.blockedRequests)}</dd></Metric>
          <Metric><dt>분석 결과 재사용</dt><dd>{fmt(usage.cacheHits)}회</dd></Metric>
        </DetailGrid></Details>
        {usage.dailyTokenLimit > 0 && <Budget>
          <BudgetLabel><span>오늘 토큰 한도 사용률</span><strong>{Math.min(100, Math.round(((usage.totalTokens + usage.reservedTokens) / usage.dailyTokenLimit) * 100))}%</strong></BudgetLabel>
          <progress aria-label="오늘 AI 토큰 한도 사용률" max={usage.dailyTokenLimit}
            value={Math.min(usage.dailyTokenLimit, usage.totalTokens + usage.reservedTokens)} />
          <Help>실제 사용량과 확인 중인 요청을 합산합니다.</Help>
        </Budget>}
        {usage.dailyTokenLimit > 0 && usage.remainingTokens === 0 && <Alert tone="warning">오늘 토큰 한도에 도달했습니다. 한도를 조정하거나 다음 날 생성 작업을 재시도할 수 있습니다.</Alert>}
        {usage.dailyImageLimit > 0 && usage.remainingImages === 0 && <Alert tone="warning">오늘 이미지 생성 한도에 도달했습니다.</Alert>}
        <Help>제공사가 보고한 실제 입력·출력 토큰입니다. 제공사 캐시는 입력 토큰에 포함되며, 분석 결과 재사용은 추가 AI 호출을 줄인 횟수입니다. 확인 중인 요청은 한도에 임시 반영됩니다. 이미지 요청과 토큰 수는 별도로 집계합니다.</Help>
      </>}
      {historyDays != null && <AiUsageHistory days={historyDays} refreshKey={`${refreshKey}:${refresh}`} />}
    </UsageSection>
  );
}

function validateLimit(value: string): string {
  if (!value.trim()) return '한도를 입력하세요. 제한 없이 사용하려면 0을 입력하세요.';
  if (!/^\d+$/.test(value.trim())) return '0 이상의 정수로 입력하세요.';
  if (!Number.isSafeInteger(Number(value)) || Number(value) > MAX_AI_DAILY_LIMIT) return `${MAX_AI_DAILY_LIMIT.toLocaleString('ko-KR')} 이하로 입력하세요.`;
  return '';
}

const UsageSection = styled.section`
  min-width: 0;
`;
const Heading = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
  h2 { font-size: 18px; font-weight: 700; letter-spacing: -0.02em; }
  button { flex-shrink: 0; }
`;
const HeadingActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const LimitsForm = styled.form`
  display: grid;
  gap: 14px;
  margin: 16px 0 20px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  h3 { font-size: 15px; }
  @media (max-width: 480px) { padding: 16px; }
`;
const LimitFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
  input { @media (max-width: 768px) { font-size: 16px; } }
`;
const FormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;
const FieldError = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.danger};
`;
const Grid = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const DetailGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  margin-bottom: 0;
`;
const Metric = styled.div<{ $emphasis?: boolean }>`
  min-width: 0;
  background: ${({ theme, $emphasis }) => $emphasis ? theme.colors.primarySoft : theme.colors.surfaceMuted};
  border: 1px solid ${({ theme, $emphasis }) => $emphasis ? theme.colors.primary + '25' : theme.colors.border};
  padding: 18px;
  border-radius: ${({ theme }) => theme.radius.md};
  dt { color: ${({ theme }) => theme.colors.textSecondary}; font-size: 13px; line-height: 1.5; }
  dd { margin: 10px 0 0; font-size: 25px; font-weight: 750; letter-spacing: -0.035em; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
`;
const MetricNote = styled.dd`
  && {
    margin: 7px 0 0;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: inherit;
  }
  margin-top: 7px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const MetricUnit = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const Help = styled.p`
  font-size: 13px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 8px;
`;
const Details = styled.details`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 0 16px;
  margin: 16px 0;
  summary { cursor: pointer; min-height: 48px; padding: 13px 0; font-size: 13px; font-weight: 650; }
  &[open] { padding-bottom: 16px; }
`;
const Budget = styled.div`
  margin: 20px 0;
  progress { display: block; width: 100%; height: 9px; border: none; border-radius: 99px; overflow: hidden; accent-color: ${({ theme }) => theme.colors.primary}; }
  progress::-webkit-progress-bar { background: ${({ theme }) => theme.colors.surfaceMuted}; border-radius: 99px; }
  progress::-webkit-progress-value { background: ${({ theme }) => theme.colors.primary}; border-radius: 99px; }
  progress::-moz-progress-bar { background: ${({ theme }) => theme.colors.primary}; border-radius: 99px; }
`;
const BudgetLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 13px;
  strong { color: ${({ theme }) => theme.colors.primary}; font-variant-numeric: tabular-nums; }
`;
const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 130px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
